import * as Types from '../types';
import { Environment } from '../../../config';
import request from '../../../utils/request';
import { LanguagesEnum } from 'constants/languages';
import FileType from 'constants/fileType';
import FileContentType from 'constants/fileContentType';

/** fetchCategoryList  */
export interface ICategoryListOutput {
  items: IDataItems[];
  paging: IPaging;
}

export interface IDescriptionData {
  language: LanguagesEnum;
  value: string;
}

export interface IDataItems {
  id: number;
  name: string;
  names: IDescriptionData[];
  description: string;
  descriptions: IDescriptionData[];
  categorytemplateid: number;
  metakeywords: string | null;
  metadescription: string | null;
  metatitle: string | null;
  parentcategoryid: number;
  pictureid: number;
  pagesize: number;
  allowcustomerstoselectpagesize: boolean;
  pagesizeoptions: string | null;
  priceranges: string | null;
  showonhomepage: boolean;
  includeintopmenu: boolean;
  subjecttoacl: boolean;
  limitedtostores: boolean;
  published: boolean;
  deleted: boolean;
  displayorder: number;
  createdonutc: string | null;
  updatedonutc: string | null;
  picture: string | null;
  storeid: number;
}

interface IPaging {
  page: string;
  pageSize: string;
  total: number;
}

interface ICategoryAll {
  languageID?: LanguagesEnum;
}

const defaultICategoryAll = {
  languageID: LanguagesEnum.English,
};

export const fetchAllCategories = async ({
  languageID,
} = defaultICategoryAll): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/category/list?language=${languageID}`
  );

  let _data: IDataItems[] = data.data.items;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export interface ICategoryListInput {
  sortField: string;
  page: number;
  pageSize: number;
  order: 'asc' | 'desc';
  name: string;
}

export const fetchCategoryList = async (
  payload: ICategoryListInput,
  languageID: LanguagesEnum
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/category/list?language=${languageID}&page=${payload.page}&pageSize=${payload.pageSize}&sortField=${payload.sortField}&order=${payload.order}&name=${payload.name}`
  );

  let _data: ICategoryListOutput = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

/** Category Detail */
interface ICategoryDetailInput {
  id: number;
  languageID: LanguagesEnum;
}

interface ICategoryDetailOutput extends Types.ResponseOutput {
  data: {
    id: number | null;
    name: string | null;
    description: IDescriptionData[] | null;
    categorytemplateid: number | null;
    metakeywords: string | null;
    metadescription: string | null;
    metatitle: string | null;
    parentcategoryid: number | null;
    pictureid: number | null;
    pagesize: number | null;
    allowcustomerstoselectpagesize: boolean;
    pagesizeoptions: string | null;
    priceranges: string | null;
    showonhomepage: boolean;
    includeintopmenu: boolean;
    subjecttoacl: boolean;
    limitedtostores: boolean;
    published: boolean;
    deleted: boolean;
    displayorder: number | null;
    createdonutc: string | null;
    updatedonutc: string | null;
    storeid: number | null;
  };
}

export const fetchCategoryDetail = async ({
  id,
  languageID,
}: ICategoryDetailInput): Promise<ICategoryDetailOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/category/${id}?language=${languageID}`
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

/** Add Category */
export interface IAddCategoryInput {
  name: string | null;
  description: string | null;
}

interface ICategoryAddOutput extends Types.ResponseOutput {
  data: {};
}

export const addCategory = async (
  payload: IAddCategoryInput
): Promise<ICategoryAddOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/category `,
    {
      body: payload,
    }
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

/** Edit Category */
export interface IDocumentPayload {
  mimetype: string;
  seofilename?: string;
  altattribute?: string;
  titleattribute?: string;
  virtualpath?: string;
  originalfilename?: string;
  filesize?: number;
  visible?: boolean;
  filetype: FileType;
  filecontenttype: FileContentType;
  displayorder?: number;
  payload?: string;
}
export interface ICategoryEditInput {
  id: number;
  name: string | null;
  description: string | null;
  categorytemplateid: number | null;
  metakeywords: string | null;
  metadescription: string | null;
  metatitle: string | null;
  parentcategoryid: number | null;
  // pictureid: number | null;
  pagesize: number | null;
  allowcustomerstoselectpagesize: string | null;
  pagesizeoptions: string | null;
  priceranges: string | null;
  showonhomepage: string | null;
  includeintopmenu: string | null;
  subjecttoacl: string | null;
  limitedtostores: string | null;
  published: string | null;
  displayorder: number | null;
  picture?: Array<IDocumentPayload> | null;
}

interface ICategoryEditOutput extends Types.ResponseOutput {
  data: {};
}

export const editCategory = async (
  payload: ICategoryEditInput
): Promise<ICategoryEditOutput> => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/category`,
    {
      body: payload,
    }
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const deleteCategory = async ({
  id,
}: {
  id: number;
}): Promise<ICategoryEditOutput> => {
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/category/${id}`
  );

  let output = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};
